import { create } from 'zustand';
import { DateTime } from 'luxon';
import { getMostRecentSundayFromDate } from 'components/BKJDatePicker/src/BKJDatePicker.utils';
import { IPayPeriodConfig } from 'components/BKJDatePicker/src/BKJDatePicker.types';
import { createDashboardsRequest } from 'pages/ReportsPage/components/AnalyticsDashboards/createDashboardsRequest';
import { DASHBOARDS_CURRENT_PAY_PERIOD } from 'constants/endpoints';
import {
  CurrentPayPeriodResponse,
  PayPeriodStore,
} from 'pages/ReportsPage/store/payPeriod.store.types';
import { devtoolsInNonProd } from 'store/zustand/index.utils';
import { ISO8601TupleType } from 'types/common.types';

const INITIAL_STATE = {
  communityId: null as Nullable<string>,
  positionId: null as Nullable<string>,
  status: 'idle' as const,
  config: null as Nullable<IPayPeriodConfig>,
  currentPeriodRange: null as Nullable<ISO8601TupleType>,
};

const toLocalCalendarDay = (value: string): DateTime => {
  const parsed = DateTime.fromISO(value, { setZone: true });
  if (!parsed.isValid) return parsed;
  return DateTime.fromISO(parsed.toISODate() ?? '');
};

export function mapPayPeriodResponse(response: CurrentPayPeriodResponse): {
  config: IPayPeriodConfig;
  currentPeriodRange: ISO8601TupleType;
} {
  const period = response?.current_pay_period;
  const start = toLocalCalendarDay(period?.pay_period_start_at ?? '').startOf('day');
  const end = toLocalCalendarDay(period?.pay_period_end_at ?? '').endOf('day');
  const periodDays = Math.round(end.startOf('day').diff(start, 'days').days) + 1;
  if (!start.isValid || !end.isValid || periodDays < 1) {
    throw new Error('Invalid pay period response');
  }
  return {
    config: { anchorDate: start.toString(), periodDays },
    currentPeriodRange: [start.toString(), end.toString()],
  };
}

export function buildFallbackPayPeriod(): {
  config: IPayPeriodConfig;
  currentPeriodRange: ISO8601TupleType;
} {
  const start = DateTime.fromISO(getMostRecentSundayFromDate(DateTime.now().toString()));
  return {
    config: { anchorDate: start.toString(), periodDays: 7 },
    currentPeriodRange: [start.toString(), start.plus({ days: 6 }).endOf('day').toString()],
  };
}

let fetchId = 0;

export const payPeriodStore = create<PayPeriodStore>()(
  devtoolsInNonProd((set, get) => ({
    ...INITIAL_STATE,

    fetchPayPeriod: (communityId, positionId) => {
      const state = get();
      const isSameCohort = state.communityId === communityId && state.positionId === positionId;
      if (isSameCohort && state.status === 'loading') {
        return;
      }

      const currentFetchId = ++fetchId;
      set({
        communityId,
        positionId,
        status: 'loading',
        config: isSameCohort ? state.config : null,
        currentPeriodRange: isSameCohort ? state.currentPeriodRange : null,
      });

      createDashboardsRequest<CurrentPayPeriodResponse>({
        url: DASHBOARDS_CURRENT_PAY_PERIOD(),
        method: 'GET',
        query: {
          pay_period_params: {
            for_scheduling_cohort: {
              community_id: Number(communityId),
              position_id: Number(positionId),
            },
          },
        },
      })
        .then(({ data }) => {
          if (currentFetchId !== fetchId) return;
          set({ status: 'ready', ...mapPayPeriodResponse(data) });
        })
        .catch(() => {
          if (currentFetchId !== fetchId) return;
          set({ status: 'fallback', ...buildFallbackPayPeriod() });
        });
    },
    reset: () => {
      fetchId++;
      set(INITIAL_STATE);
    },
  })),
);

export const usePayPeriodStore = payPeriodStore;
